import * as React from "react";
import LoadingButton from "@mui/lab/LoadingButton";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { colors, useMediaQuery, useTheme } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useLoginMutation } from "../../services/auth.service";
import { Navigate, useNavigate } from "react-router-dom";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { isProductionMode } from "../../utils/helper";
import CornerRibbon from "../../components/CornerRibbon";
import workz360 from "../../images/workz360.png";
import { StyledTextField, CardBase } from "../../components/global";

const StyledLoadingButton = styled(LoadingButton)(({ theme }) => ({
  borderRadius: 12,
  padding: "12px 24px",
  fontSize: "1rem",
  fontWeight: 600,
  textTransform: "none",
  backgroundColor: "#12903C",
  color: "#ffffff",
  boxShadow: "0 4px 14px rgba(18, 144, 60, 0.4)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    backgroundColor: "#0f7a32",
    transform: "translateY(-2px)",
    boxShadow: "0 6px 20px rgba(18, 144, 60, 0.6)",
  },
  "&:active": {
    transform: "translateY(0)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "10px 20px",
    fontSize: "0.875rem",
  },
}));


function Copyright(props: any) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      sx={{ opacity: 0.7, fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
      {...props}
    >
      {"Copyright © "}
      <Link
        color="inherit"
        href="https://workz360.com/"
        sx={{
          textDecoration: "none",
          fontWeight: 500,
          "&:hover": {
            textDecoration: "underline",
          },
        }}
      >
        Workz360
      </Link>{" "}
      {new Date().getFullYear()}
      {"."}
    </Typography>
  );
}

function SignupLink(props: any) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
      {...props}
    >
      {"Don't have an account? "}
      <Link
        variant="body2"
        href="/signup"
        sx={{
          fontWeight: 600,
          textDecoration: "none",
          color: "#12903C",
          "&:hover": {
            textDecoration: "underline",
          },
        }}
      >
        Sign Up
      </Link>
    </Typography>
  );
}

export default function LoginPage() {
  const [login, { isLoading }] = useLoginMutation();
  const user = useCurrentUser();
  const navigate = useNavigate();
  const isProduction = React.useMemo(() => isProductionMode(), []);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
  } = useForm();

  // @ts-ignore
  const onSubmit = async (data: any) => {
    const { email, password } = data;
    await login({ username: email, password });
  };

  if (!!user) return <Navigate to="/dashboard" replace={true} />;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        height: { xs: "100%", sm: "100vh" },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundImage: "url('/background-home.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: { xs: "scroll", sm: "fixed" },
        px: { xs: 2, sm: 3 },
        py: { xs: 3, sm: 4 },
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: { xs: "100%", sm: 480 },
          position: "relative",
        }}
      >
        <Box sx={{ position: "relative" }}>
          {!isProduction && (
            <CornerRibbon
              position="top-right"
              backgroundColor={colors.yellow[200]}
              fontColor="#333"
            >
              STAGING
            </CornerRibbon>
          )}
          <CardBase
            useGlassCard={true}
            nested={false}
            sx={{
              p: { xs: 2.5, sm: 4, md: 5 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                mb: { xs: 2, sm: 3 },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: { xs: 0.75, sm: 1 },
              }}
            >
              <Box
                component="img"
                src={workz360}
                alt="Workz360"
                sx={{
                  height: { xs: 44, sm: 56 },
                  width: "auto",
                  objectFit: "contain",
                  filter: "drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1))",
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ 
                  mt: { xs: 0.25, sm: 0.5 }, 
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  textAlign: "center",
                }}
              >
                Sign in to access your account
              </Typography>
            </Box>

            <Box
              component="form"
              noValidate
              onSubmit={handleSubmit(onSubmit)}
              sx={{ width: "100%", mt: { xs: 1.5, sm: 2 } }}
            >
              <Controller
                name="email"
                defaultValue=""
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <StyledTextField
                    margin="normal"
                    fullWidth
                    id="email"
                    size={isMobile ? "small" : "medium"}
                    label="Email Address"
                    autoComplete="email"
                    autoFocus
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                    inputRef={ref}
                    error={!!errors.email}
                    helperText={!!errors.email && "This field is required"}
                  />
                )}
              />
              <Controller
                name="password"
                defaultValue=""
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <StyledTextField
                    margin="normal"
                    fullWidth
                    label="Password"
                    type="password"
                    id="password"
                    size={isMobile ? "small" : "medium"}
                    autoComplete="current-password"
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                    inputRef={ref}
                    error={!!errors.password}
                    helperText={!!errors.password && "This field is required"}
                  />
                )}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: { xs: 0.75, sm: 1 } }}>
                <Link
                  href="/forgot-password"
                  variant="body2"
                  sx={{
                    textDecoration: "none",
                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                    fontWeight: 500,
                    color: "#12903C",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  Forgot password?
                </Link>
              </Box>
              <StyledLoadingButton
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 1.5, sm: 2 } }}
                loading={isLoading}
              >
                Sign In
              </StyledLoadingButton>
              <SignupLink sx={{ mt: { xs: 2, sm: 3 } }} />
              <Copyright sx={{ mt: { xs: 3, sm: 4 } }} />
            </Box>
          </CardBase>
        </Box>
      </Box>
    </Box>
  );
}
